import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import {
  Plus, ArrowLeft, Save, Loader2, Sparkles, Trash2, Calendar, Users,
  ChevronRight, CheckCircle2, ClipboardList, Lightbulb, Phone, Download,
  Heart, Building2, Baby, PartyPopper, UtensilsCrossed, Truck, X, PlusCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  Mariage: Heart,
  Entreprise: Building2,
  Bapteme: Baby,
  Anniversaire: PartyPopper,
  Restaurant: UtensilsCrossed,
  Traiteur: Truck,
};

const TYPE_COLORS = {
  Mariage: { bg: 'bg-pink-500/10', text: 'text-pink-600', border: 'border-pink-500/20', accent: '#ec4899' },
  Entreprise: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-500/20', accent: '#3b82f6' },
  Bapteme: { bg: 'bg-sky-500/10', text: 'text-sky-600', border: 'border-sky-500/20', accent: '#0ea5e9' },
  Anniversaire: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/20', accent: '#f59e0b' },
  Restaurant: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/20', accent: '#10b981' },
  Traiteur: { bg: 'bg-orange-500/10', text: 'text-orange-600', border: 'border-orange-500/20', accent: '#f97316' },
};

function Toggle({ checked, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
      data-testid={testId}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  );
}

export default function RdvCommercial() {
  const [view, setView] = useState('list');
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [formData, setFormData] = useState({});
  const [hiddenFields, setHiddenFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [proposition, setProposition] = useState(null);
  const [checklists, setChecklists] = useState({});
  const [iaScore, setIaScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(false);
  const [emailData, setEmailData] = useState(null);
  const [smsData, setSmsData] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingSms, setLoadingSms] = useState(false);
  const [savedPropositions, setSavedPropositions] = useState([]);

  const loadRdvs = useCallback(async () => {
    try {
      const [rRes, cRes] = await Promise.all([
        fetch(`${API}/api/propositions/rdv-commerciaux`),
        fetch(`${API}/api/propositions/rdv-commercial/checklists`),
      ]);
      setRdvs(await rRes.json());
      setChecklists(await cRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadRdvs(); }, [loadRdvs]);

  const selectType = async (type) => {
    setSelectedType(type);
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/checklist/${type}`);
      setChecklist(await r.json());
      setFormData({});
      setHiddenFields({});
      setView('form');
    } catch { toast.error('Erreur chargement'); }
  };

  const openRdv = async (rdv) => {
    setEditingRdv(rdv);
    setSelectedType(rdv.type_evenement);
    setFormData(rdv.donnees || {});
    setHiddenFields(rdv.hidden_fields || {});
    setIaScore(null); setEmailData(null); setSmsData(null); setProposition(null);
    try {
      const [rCheck, rProps] = await Promise.all([
        fetch(`${API}/api/propositions/rdv-commercial/checklist/${rdv.type_evenement}`),
        fetch(`${API}/api/propositions/rdv-commercial/${rdv.id}/propositions`),
      ]);
      setChecklist(await rCheck.json());
      setSavedPropositions(await rProps.json());
      setView('form');
    } catch { toast.error('Erreur'); }
  };

  const updateField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const hideField = (key) => {
    setHiddenFields(prev => ({ ...prev, [key]: true }));
    setFormData(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const restoreField = (key) => {
    setHiddenFields(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const saveRdv = async () => {
    setSaving(true);
    try {
      const body = {
        id: editingRdv?.id,
        type_evenement: selectedType,
        donnees: formData,
        hidden_fields: hiddenFields,
        statut: formData.statut || editingRdv?.statut || 'nouveau',
      };
      const r = await fetch(`${API}/api/propositions/rdv-commercial`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const saved = await r.json();
        setEditingRdv(saved);
        toast.success('RDV sauvegarde');
        loadRdvs();
      }
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  };

  const deleteRdv = async (id) => {
    if (!window.confirm('Supprimer ce RDV ?')) return;
    try {
      await fetch(`${API}/api/propositions/rdv-commercial/${id}`, { method: 'DELETE' });
      toast.success('Supprime');
      loadRdvs();
    } catch {}
  };

  const generateProposition = async () => {
    if (!editingRdv?.id) { toast.error("Sauvegardez d'abord"); return; }
    setGenerating(true); setProposition(null);
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/generate-proposition`, { method: 'POST' });
      if (r.ok) { setProposition(await r.json()); toast.success('Proposition generee'); }
      else toast.error('Erreur generation');
    } catch { toast.error('Erreur connexion'); }
    setGenerating(false);
  };

  const loadIaScore = async () => {
    if (!editingRdv?.id) return;
    setLoadingScore(true);
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/ia-score`, { method: 'POST' });
      if (r.ok) setIaScore(await r.json());
    } catch {}
    setLoadingScore(false);
  };

  const generateEmail = async (type = 'suivi') => {
    if (!editingRdv?.id) return;
    setLoadingEmail(true); setEmailData(null);
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/ia-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      if (r.ok) { setEmailData(await r.json()); toast.success('Email genere'); }
    } catch { toast.error('Erreur'); }
    setLoadingEmail(false);
  };

  const generateSms = async () => {
    if (!editingRdv?.id) return;
    setLoadingSms(true); setSmsData(null);
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/ia-sms`, { method: 'POST' });
      if (r.ok) { setSmsData(await r.json()); toast.success('SMS genere'); }
    } catch {}
    setLoadingSms(false);
  };

  const saveProposition = async () => {
    if (!editingRdv?.id || !proposition) return;
    try {
      const d = editingRdv.donnees || formData;
      const name = d.nom_couple || d.entreprise || d.nom_contact || d.nom_famille || '';
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/save-proposition`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...proposition, client_nom: name }),
      });
      if (r.ok) { toast.success('Proposition sauvegardee'); loadPropositions(); }
    } catch { toast.error('Erreur'); }
  };

  const loadPropositions = async () => {
    if (!editingRdv?.id) return;
    try {
      const r = await fetch(`${API}/api/propositions/rdv-commercial/${editingRdv.id}/propositions`);
      if (r.ok) setSavedPropositions(await r.json());
    } catch {}
  };

  const goBack = () => { setView('list'); setSelectedType(null); setChecklist(null); setFormData({}); setHiddenFields({}); setEditingRdv(null); setProposition(null); setIaScore(null); setEmailData(null); setSmsData(null); setSavedPropositions([]); };

  const getCompletion = (data, cl) => {
    if (!cl?.sections) return 0;
    const required = cl.sections.flatMap(s => s.fields).filter(f => f.required);
    const filled = required.filter(f => data[f.key] && String(data[f.key]).trim());
    return required.length > 0 ? Math.round((filled.length / required.length) * 100) : 100;
  };

  // ═══ TYPE SELECTION ═══
  if (view === 'new-type') {
    return (
      <div className="space-y-4" data-testid="rdv-type-select">
        <button onClick={goBack} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="text-center mb-6">
          <h2 className="text-lg font-bold text-[rgb(var(--lm-text))]">Nouveau RDV Commercial</h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Quel type d'evenement ?</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(TYPE_ICONS).map(([type, Icon]) => {
            const colors = TYPE_COLORS[type];
            return (
              <button key={type} onClick={() => selectType(type)}
                className={`${colors.bg} border ${colors.border} rounded-xl p-4 text-center hover:shadow-md transition-all group`}
                data-testid={`rdv-type-${type.toLowerCase()}`}>
                <Icon size={24} className={`${colors.text} mx-auto mb-2 group-hover:scale-110 transition-transform`} />
                <p className={`text-xs font-bold ${colors.text}`}>{checklists[type]?.label || type}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ═══ FORM VIEW ═══
  if (view === 'form' && checklist) {
    const completion = getCompletion(formData, checklist);
    const colors = TYPE_COLORS[selectedType] || TYPE_COLORS.Entreprise;
    const Icon = TYPE_ICONS[selectedType] || Building2;

    // Collect hidden fields for restore
    const allHiddenKeys = checklist.sections.flatMap(s => s.fields).filter(f => hiddenFields[f.key]);

    return (
      <div className="space-y-4" data-testid="rdv-form">
        <div className="flex items-center justify-between">
          <button onClick={goBack} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
            <ArrowLeft size={14} /> Retour
          </button>
          <div className="flex gap-2">
            <Button onClick={saveRdv} disabled={saving} size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-8" data-testid="rdv-save">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
              Sauvegarder
            </Button>
            {editingRdv?.id && (
              <>
                <Button onClick={generateProposition} disabled={generating} size="sm" variant="outline" className="text-xs h-8 border-[rgb(var(--lm-border))]" data-testid="rdv-generate-prop">
                  {generating ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
                  Proposition IA
                </Button>
                <a href={`${API}/api/propositions/rdv-commercial/${editingRdv.id}/pdf`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="text-xs h-8 border-[rgb(var(--lm-border))]" data-testid="rdv-pdf">
                    <Download size={12} className="mr-1" /> PDF
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>

        {/* Header */}
        <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 flex items-center gap-3`}>
          <Icon size={24} className={colors.text} />
          <div className="flex-1">
            <h2 className={`text-sm font-bold ${colors.text}`}>{checklist.label}</h2>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">
              {editingRdv ? `Modifie le ${new Date(editingRdv.updated_at).toLocaleDateString('fr-FR')}` : 'Nouveau RDV'}
            </p>
          </div>
          <div className="text-right">
            <p className={`text-xl font-bold ${colors.text}`}>{completion}%</p>
            <p className="text-[8px] text-[rgb(var(--lm-text-muted))]">completude</p>
          </div>
        </div>

        {/* Suggestions */}
        {checklist.suggestions?.length > 0 && (
          <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/30 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Lightbulb size={13} className="text-amber-500" />
              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider">A ne pas oublier</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {checklist.suggestions.map((s, i) => (
                <p key={i} className="text-[10px] text-amber-800/70 dark:text-amber-300/70 flex items-start gap-1.5">
                  <CheckCircle2 size={10} className="shrink-0 mt-0.5 text-amber-400" /> {s}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Sections */}
        {checklist.sections?.map((section, si) => {
          const visibleFields = section.fields.filter(f => !hiddenFields[f.key]);
          const hiddenInSection = section.fields.filter(f => hiddenFields[f.key]);

          return (
            <div key={si} className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[rgb(var(--lm-border))]/15 bg-[rgb(var(--lm-surface))]/50">
                <h3 className="text-[11px] font-bold text-[rgb(var(--lm-text))] uppercase tracking-wider">{section.title}</h3>
              </div>
              <div className="p-3 space-y-2.5">
                {visibleFields.map((field) => (
                  <div key={field.key} className="flex items-start gap-2">
                    <div className="flex-1 flex flex-col gap-1">
                      <label className="text-[10px] text-[rgb(var(--lm-text-muted))] flex items-center gap-1">
                        {field.label}
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      {field.type === 'toggle' ? (
                        <div className="flex items-center gap-2" data-testid={`field-${field.key}`}>
                          <Toggle checked={!!formData[field.key]} onChange={v => updateField(field.key, v)} testId={`toggle-${field.key}`} />
                          <span className="text-xs text-[rgb(var(--lm-text))]">{formData[field.key] ? 'Oui' : 'Non'}</span>
                        </div>
                      ) : field.type === 'textarea' ? (
                        <textarea value={formData[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                          rows={3} className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--lm-gold))]/30 resize-none"
                          data-testid={`field-${field.key}`} />
                      ) : field.type === 'select' ? (
                        <select value={formData[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                          className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] focus:outline-none"
                          data-testid={`field-${field.key}`}>
                          <option value="">-- Choisir --</option>
                          {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : field.type === 'checkbox' ? (
                        <div className="flex items-center gap-2" data-testid={`field-${field.key}`}>
                          <Toggle checked={!!formData[field.key]} onChange={v => updateField(field.key, v)} testId={`toggle-${field.key}`} />
                          <span className="text-xs text-[rgb(var(--lm-text))]">{formData[field.key] ? 'Oui' : 'Non'}</span>
                        </div>
                      ) : (
                        <Input value={formData[field.key] || ''} onChange={e => updateField(field.key, e.target.value)}
                          type={field.type || 'text'} className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-8"
                          data-testid={`field-${field.key}`} />
                      )}
                    </div>
                    {!field.required && (
                      <button onClick={() => hideField(field.key)} className="mt-5 p-1 rounded hover:bg-red-500/10 text-[rgb(var(--lm-text-muted))] hover:text-red-400 transition-colors"
                        title="Supprimer ce champ" data-testid={`hide-${field.key}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}

                {/* Restore hidden fields */}
                {hiddenInSection.length > 0 && (
                  <div className="pt-2 border-t border-[rgb(var(--lm-border))]/10">
                    <div className="flex flex-wrap gap-1">
                      {hiddenInSection.map(f => (
                        <button key={f.key} onClick={() => restoreField(f.key)}
                          className="flex items-center gap-1 text-[9px] px-2 py-1 rounded-md bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))] border border-[rgb(var(--lm-border))]/20 transition-colors"
                          data-testid={`restore-${f.key}`}>
                          <PlusCircle size={10} /> {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* ═══ IA SCORE + SUGGESTIONS (Phase 2) ═══ */}
        {editingRdv?.id && (
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl overflow-hidden" data-testid="ia-block">
            <div className="px-4 py-2.5 border-b border-[rgb(var(--lm-border))]/15 bg-gradient-to-r from-purple-500/5 to-blue-500/5 flex items-center justify-between">
              <h3 className="text-[11px] font-bold text-[rgb(var(--lm-text))] uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles size={12} className="text-purple-500" /> Analyse IA
              </h3>
              <Button onClick={loadIaScore} disabled={loadingScore} size="sm" variant="ghost" className="text-[10px] h-6 px-2" data-testid="ia-score-btn">
                {loadingScore ? <Loader2 size={10} className="animate-spin" /> : 'Analyser'}
              </Button>
            </div>

            {iaScore && (
              <div className="p-3 space-y-3">
                {/* Score bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Score de conversion</span>
                      <span className={`text-xs font-bold ${iaScore.level === 'chaud' ? 'text-red-500' : iaScore.level === 'tiede' ? 'text-amber-500' : 'text-blue-400'}`}>
                        {iaScore.score}% {iaScore.level === 'chaud' ? '— Chaud' : iaScore.level === 'tiede' ? '— Tiede' : '— Froid'}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-[rgb(var(--lm-bg))] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${iaScore.level === 'chaud' ? 'bg-red-500' : iaScore.level === 'tiede' ? 'bg-amber-500' : 'bg-blue-400'}`}
                        style={{ width: `${iaScore.score}%` }} />
                    </div>
                  </div>
                </div>
                {/* Reasons */}
                {iaScore.reasons?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {iaScore.reasons.map((r, i) => (
                      <span key={i} className="text-[8px] bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text-muted))] px-2 py-0.5 rounded-full">{r}</span>
                    ))}
                  </div>
                )}
                {/* Suggestions */}
                {iaScore.suggestions?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-[9px] font-bold text-purple-500 uppercase tracking-wider">Suggestions</p>
                    {iaScore.suggestions.map((s, i) => (
                      <p key={i} className="text-[10px] text-[rgb(var(--lm-text-secondary))] flex items-start gap-1.5">
                        <Sparkles size={9} className="shrink-0 mt-0.5 text-purple-400" /> {s}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="px-3 pb-3 flex flex-wrap gap-1.5">
              <Button onClick={generateProposition} disabled={generating} size="sm" variant="outline" className="text-[10px] h-7 border-[rgb(var(--lm-border))]" data-testid="ia-proposition">
                {generating ? <Loader2 size={10} className="animate-spin mr-1" /> : <ClipboardList size={10} className="mr-1" />}
                Generer proposition
              </Button>
              <Button onClick={() => generateEmail('suivi')} disabled={loadingEmail} size="sm" variant="outline" className="text-[10px] h-7 border-[rgb(var(--lm-border))]" data-testid="ia-email-suivi">
                {loadingEmail ? <Loader2 size={10} className="animate-spin mr-1" /> : <span className="mr-1">@</span>}
                Email suivi
              </Button>
              <Button onClick={() => generateEmail('relance')} disabled={loadingEmail} size="sm" variant="outline" className="text-[10px] h-7 border-[rgb(var(--lm-border))]" data-testid="ia-email-relance">
                Email relance
              </Button>
              <Button onClick={generateSms} disabled={loadingSms} size="sm" variant="outline" className="text-[10px] h-7 border-[rgb(var(--lm-border))]" data-testid="ia-sms">
                {loadingSms ? <Loader2 size={10} className="animate-spin mr-1" /> : <Phone size={10} className="mr-1" />}
                SMS confirmation
              </Button>
            </div>
          </div>
        )}

        {/* Email generated */}
        {emailData && (
          <div className="bg-[rgb(var(--lm-card))] border border-blue-500/20 rounded-xl p-4 space-y-2" data-testid="email-result">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-bold text-blue-500 uppercase tracking-wider">Email genere</span>
              <button onClick={() => { navigator.clipboard.writeText(`Objet: ${emailData.subject}\n\n${emailData.body}`); toast.success('Copie !'); }}
                className="text-[9px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">Copier</button>
            </div>
            <p className="text-xs font-bold text-[rgb(var(--lm-text))]">{emailData.subject}</p>
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] whitespace-pre-wrap leading-relaxed">{emailData.body}</p>
          </div>
        )}

        {/* SMS generated */}
        {smsData && (
          <div className="bg-[rgb(var(--lm-card))] border border-emerald-500/20 rounded-xl p-4 space-y-2" data-testid="sms-result">
            <span className="text-[9px] font-bold text-emerald-500 uppercase tracking-wider">SMS genere</span>
            <p className="text-xs text-[rgb(var(--lm-text))]">{smsData.sms}</p>
            {smsData.to && <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">Destinataire : {smsData.to}</p>}
            <button onClick={() => { navigator.clipboard.writeText(smsData.sms); toast.success('Copie !'); }}
              className="text-[9px] text-emerald-500 hover:underline">Copier le SMS</button>
          </div>
        )}

        {/* Generated Proposition */}
        {proposition && (
          <div className="bg-[rgb(var(--lm-card))] border-2 border-[rgb(var(--lm-gold))]/30 rounded-xl overflow-hidden" data-testid="rdv-proposition">
            <div className="px-4 py-3 bg-[rgb(var(--lm-gold))]/10 border-b border-[rgb(var(--lm-gold))]/20 flex items-center justify-between">
              <h3 className="text-xs font-bold text-[rgb(var(--lm-text))] flex items-center gap-1.5">
                <Sparkles size={14} className="text-[rgb(var(--lm-gold))]" /> Proposition IA
              </h3>
              <div className="flex gap-1.5">
                <Button onClick={saveProposition} size="sm" variant="outline" className="text-[9px] h-6 px-2 border-[rgb(var(--lm-gold))]/30" data-testid="save-proposition">
                  <Save size={10} className="mr-1" /> Sauvegarder
                </Button>
                <a href={`${API}/api/propositions/rdv-commercial/${editingRdv?.id}/pdf`} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="text-[9px] h-6 px-2"><Download size={10} className="mr-1" /> PDF</Button>
                </a>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {proposition.resume && <p className="text-xs text-[rgb(var(--lm-text))] leading-relaxed">{proposition.resume}</p>}
              {proposition.prestations?.length > 0 && (
                <div className="space-y-1">
                  {proposition.prestations.map((p, i) => (
                    <div key={i} className="flex justify-between items-center bg-[rgb(var(--lm-bg))] rounded-lg px-3 py-2">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-[rgb(var(--lm-text))]">{p.nom}</p>
                        {p.raison && <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{p.raison}</p>}
                      </div>
                      <p className="text-xs font-bold text-[rgb(var(--lm-text))]">{p.total || p.prix_unitaire}EUR</p>
                    </div>
                  ))}
                </div>
              )}
              {proposition.total_estime > 0 && (
                <div className="bg-[rgb(var(--lm-navy))]/5 rounded-xl p-3 text-center">
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">TOTAL ESTIME</p>
                  <p className="text-xl font-bold" style={{ color: colors.accent }}>{proposition.total_estime}EUR</p>
                </div>
              )}
              {proposition.message_client && (
                <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 text-xs text-[rgb(var(--lm-text))] whitespace-pre-wrap border border-[rgb(var(--lm-border))]/20">
                  {proposition.message_client}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Saved propositions history */}
        {savedPropositions.length > 0 && (
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-3" data-testid="propositions-history">
            <p className="text-[9px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">Historique propositions</p>
            {savedPropositions.map(p => (
              <div key={p.id} className="flex items-center justify-between py-1.5 border-b border-[rgb(var(--lm-border))]/10 last:border-0">
                <div>
                  <span className="text-[10px] font-bold text-[rgb(var(--lm-text))]">v{p.version}</span>
                  <span className="text-[9px] text-[rgb(var(--lm-text-muted))] ml-2">{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${p.statut === 'envoyee' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {p.statut}
                  </span>
                  {p.total_estime > 0 && <span className="text-[10px] font-mono text-[rgb(var(--lm-text))]">{p.total_estime}EUR</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button onClick={saveRdv} disabled={saving} className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-9" data-testid="rdv-save-bottom">
          {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
          Sauvegarder
        </Button>
      </div>
    );
  }

  // ═══ LIST VIEW ═══
  return (
    <div className="space-y-4" data-testid="rdv-commercial-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-[rgb(var(--lm-gold))]" />
          <div>
            <h2 className="text-sm font-bold text-[rgb(var(--lm-text))]">RDV Commerciaux</h2>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{rdvs.length} fiche(s)</p>
          </div>
        </div>
        <Button onClick={() => setView('new-type')} size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-8" data-testid="rdv-new">
          <Plus size={12} className="mr-1" /> Nouveau RDV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : rdvs.length === 0 ? (
        <div className="text-center py-12 bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30">
          <ClipboardList size={32} className="text-[rgb(var(--lm-text-muted))]/20 mx-auto mb-3" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucun RDV commercial</p>
          <Button onClick={() => setView('new-type')} size="sm" className="mt-4 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-8">
            <Plus size={12} className="mr-1" /> Nouveau RDV
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {rdvs.map((rdv) => {
            const cl = TYPE_COLORS[rdv.type_evenement] || TYPE_COLORS.Entreprise;
            const Ic = TYPE_ICONS[rdv.type_evenement] || Building2;
            const d = rdv.donnees || {};
            const name = d.nom_couple || d.entreprise || d.nom_famille || d.nom_contact || 'Sans nom';
            const date = d.date_mariage || d.date_event || d.date_ceremonie || '';

            return (
              <div key={rdv.id} onClick={() => openRdv(rdv)}
                className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow cursor-pointer group"
                data-testid={`rdv-item-${rdv.id}`}>
                <div className={`w-10 h-10 rounded-lg ${cl.bg} flex items-center justify-center shrink-0`}>
                  <Ic size={18} className={cl.text} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-[rgb(var(--lm-text))] truncate">{name}</p>
                    <span className={`text-[8px] ${cl.bg} ${cl.text} px-1.5 py-0.5 rounded-full font-bold`}>{rdv.type_evenement}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {date && <span className="text-[9px] text-[rgb(var(--lm-text-muted))] flex items-center gap-0.5"><Calendar size={9} /> {new Date(date).toLocaleDateString('fr-FR')}</span>}
                    {(d.nb_adultes || d.nb_personnes) && <span className="text-[9px] text-[rgb(var(--lm-text-muted))] flex items-center gap-0.5"><Users size={9} /> {d.nb_adultes || d.nb_personnes} pers.</span>}
                    {d.tel && <span className="text-[9px] text-[rgb(var(--lm-text-muted))] flex items-center gap-0.5"><Phone size={9} /> {d.tel}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); deleteRdv(rdv.id); }}
                    className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-red-400 transition-all"
                    data-testid={`rdv-delete-${rdv.id}`}>
                    <Trash2 size={12} />
                  </button>
                  <ChevronRight size={14} className="text-[rgb(var(--lm-text-muted))]" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
